import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export function useLocationSearch(query: string, isOpen: boolean) {
  const debouncedQuery = useDebounce(query, 500);

  const { data: suggestions = [], isFetching: isSearching } = useQuery({
    queryKey: ['locationSearch', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) return [];
      const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(debouncedQuery)}&count=5&language=en&format=json`);
      if (!res.ok) throw new Error('API Error');
      const data = await res.json();
      if (data.results) {
        const formatted = data.results.map((r: any) => `${r.name}, ${r.country}`);
        return Array.from(new Set(formatted)) as string[];
      }
      return [];
    },
    enabled: isOpen && debouncedQuery.length >= 2,
    staleTime: 1000 * 60 * 60, // cache for 1 hour
  });

  return { suggestions, isSearching: isSearching && query.length >= 2 };
}

export function useDetectLocation(onChange: (loc: string) => void, onLocateResult: (loc: string) => void) {
  const [errorMsg, setErrorMsg] = useState('');
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);

  const mutation = useMutation({
    mutationFn: async ({ latitude, longitude }: { latitude: number, longitude: number }) => {
      const res = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
      if (!res.ok) throw new Error('API Error');
      return await res.json();
    },
    onSuccess: (data) => {
      let loc = '';
      if (data.city && data.countryName) {
        loc = `${data.city}, ${data.countryName}`;
      } else if (data.countryName) {
        loc = data.countryName;
      }
      
      if (loc) {
        onChange(loc);
        onLocateResult(loc);
      }
    },
    onError: (error) => {
      setErrorMsg('Failed to reverse geocode.');
      setTimeout(() => setErrorMsg(''), 4000);
    }
  });

  const detectLocation = () => {
    setErrorMsg('');
    setIsRequestingPermission(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setIsRequestingPermission(false);
          mutation.mutate({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        }, 
        () => {
          setIsRequestingPermission(false);
          setErrorMsg('Location permission denied.');
          setTimeout(() => setErrorMsg(''), 4000);
        }
      );
    } else {
      setIsRequestingPermission(false);
      setErrorMsg('Geolocation not supported.');
      setTimeout(() => setErrorMsg(''), 4000);
    }
  };

  return { 
    detectLocation, 
    isLocating: mutation.isPending || isRequestingPermission, 
    errorMsg, 
    setErrorMsg 
  };
}
